// @ts-strict-ignore
import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { LMFieldBaseComponent } from '@field/lm-field-base.component';
import { LMModel } from '@form/model';
import { LMFieldServiceAdapter } from '@field/lm-field-service-adapter';
import { LMThemeService } from '@banner/lm-theme.service';
import { AppConstants } from '@utils/app.constants';
import {Observable, of} from 'rxjs';
import {map, startWith} from "rxjs/operators";

export class LMTextEvent implements LMFieldEvent {
  layoutObject: LMButtonLayout;
  event: Event;
  eventType: LMFieldEventType;

  constructor(event: Event, layoutObject: LMButtonLayout) {
    this.eventType = 'TextLinkEvent';
    this.event = event;
    this.layoutObject = layoutObject;
  }
}

enum LMTextTypeDisplayEnum {
  PLAIN = 0,
  MASK,
  PLAIN_FORM_TEXT,
  HTTP_FORM_TEXT,
  DISPLAY_AS_TEXT,
  DISPLAY_AS_TAG
}

@Directive()
export abstract class LMTextBaseComponent extends LMFieldBaseComponent implements AfterViewInit, OnDestroy {
  @Input() layoutObject: LMFormLayoutField;
  @Input() specFields: LMSpecFields;
  @Input() model: LMModel;

  @Output() fieldEvent: EventEmitter<LMFieldEvent> = new EventEmitter<LMFieldEvent>();

  @HostBinding('attr.type') isPassword = 'text';

  @HostBinding('attr.data-automation-id') dataFieldAutomationId = '';

  @HostBinding('class.lm-form-text-container') hasFormTextChild = false;

  public hasRichTextVars = false;
  public filterSettings: Array<SohoFieldFilterOption>;
  public launchButtonTooltip: string;
  public hasContextMenu$: Observable<boolean>;
  public messageIcon: string = null;
  public iconUrl: string;
  public validSohoIcon: boolean;
  public typeObserver: MutationObserver;
  public alternativeAriaLabel: string;

  public displayType: LMTextTypeDisplayEnum;
  public layoutDisplayType = LMTextTypeDisplayEnum;

  protected useNgModelChangedForMask = false;
  protected ngModelChangedValue = undefined;

  constructor(
    protected lmFieldServiceAdapter: LMFieldServiceAdapter,
    public elementRef: ElementRef,
    public lmThemeService: LMThemeService
  ) {
    super(lmFieldServiceAdapter);
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    if (this.isPassword === 'password') {
      const inputEl = this.elementRef.nativeElement.querySelector('input[type="password"]');
      if (inputEl) {
        this.typeObserver = new MutationObserver(() => {
          if (inputEl.getAttribute('type') !== 'password') {
            inputEl.setAttribute('type', 'password');
          }
        });
        this.typeObserver.observe(inputEl, { attributes: true, attributeFilter: [ 'type' ] });
      }
    }
  }

  // events
  public onBlur(event) {
    // if not onSearchForm and hasFieldChanged, then field changed while it had focus - update the model
    // or is onSearchForm - update the model
    if ((!this.onSearchForm && this.hasFieldChanged) || this.onSearchForm) {

      // used to transform BCD and PERCENT types so they have trailing zeros before leaving the field
      this.updateModelField();
    }

    super.onBlur(event);
  }

  public onSearchKeypress(event) {
    // listen for enter key
    if (event.which === 13 && event.target.nodeName !== 'BUTTON') {

      // used to transform and store the BCD or PERCENT value before the onSearchClicked() event is executed
      this.updateModelField();
    }
  }

  public onNgModelChange(value) {
    // Fields need to format the value according to the type. For example, any trailing zeros are displayed or uppercase
    // place value into temporary location until the blur event is executed
    // value is then transformed into data format and placed into modelField.value
    this.ngModelChangedValue = value;
  }

  onFiltered(event: SohoFieldFilteredEvent) {
    super.onFiltered(event);
    if (this.onSearchForm) {
      this.updateModelField();
    }
  }


  contextMenuClicked = ($event) => {
    this.onContextMenuEvent($event);
  };

  openCompactDialog(event) {
    this.onContextMenuEvent({ args: [ event.currentTarget ] });
  }

  protected textInitialize(): void {
    if (!this.field.displayAsText) {
      this.hasContextMenu$ = of(this.insertContextMenu && !(this.isFormText && !this.field.hasHttpUrl));
    } else {
      this.hasContextMenu$ = this.model.modelChanged.pipe(
        startWith(!this.modelField.isBlank),
        map(() => !this.modelField.isBlank && this.insertContextMenu)
      );
    }

    this.launchButtonTooltip = Soho.Locale.translate('Launch');

    if (this.specField) {
      super.setNumberFormat();
      super.setSearchFieldNumberFormat();
      super.setSize();
      super.setFieldClasses();
      super.setType();
    }

    if (this.layoutObject.icon) {
      this.validSohoIcon = this.lmFieldServiceAdapter.lmThemeService.isValidSohoIcon(this.layoutObject.icon);

      this.messageIcon = this.layoutObject.icon;
      this.iconUrl = this.layoutObject.iconUrl;
    }

    if (!(this.displayAsText || this.displayAsTag || this.isFormText)) {
      super.subscribeToInlineError();
    }

    if (this.field.text && this.field.text.hasOwnProperty('hasRichTextVars')) {
      this.hasRichTextVars = true;
    }

    if (!this.displayAsText && !this.displayAsTag && !this.isFormText && !this.hasMask) {
      this.displayType = LMTextTypeDisplayEnum.PLAIN;

    } else if (!this.displayAsText && !this.displayAsTag && !this.isFormText && this.hasMask) {
      this.displayType = LMTextTypeDisplayEnum.MASK;
      this.useNgModelChangedForMask = true;
      if (this.modelField?.filterCriteria?.operator) {
        this.modelField.filterType = this.modelField?.filterCriteria?.operator;
      } else {
        if (this.specField?.defaultFilterOperator) {
          this.modelField.filterType = this.specField.defaultFilterOperator;
        } else {
          this.modelField.filterType = 'equals';
        }
      }
    } else if (this.isFormText && !this.displayAsTag) {
      if (this.field.hasHttpUrl || this.layoutObject.linkNavigation) {
        this.displayType = LMTextTypeDisplayEnum.HTTP_FORM_TEXT;

      } else {
        this.displayType = LMTextTypeDisplayEnum.PLAIN_FORM_TEXT;
      }

    } else if (this.displayAsTag && !this.isCompoundFieldMember) {
      this.displayType = LMTextTypeDisplayEnum.DISPLAY_AS_TAG;

    } else if (this.displayAsText && !this.isCompoundFieldMember) {
      this.displayType = LMTextTypeDisplayEnum.DISPLAY_AS_TEXT;
    }

    this.setDefaultFieldStyle();

    if (this.layoutObject.textSearchable) {
      // Text searchable fields have the operand of startsWith, which
      // is translated into a contains search in the back end for
      // performance reasons.  Contact Joe Schreier for more information.
      this.filterSettings = this.textSearchableSettings;
      this.modelField.filterType = this.modelField?.filterCriteria?.operator || 'startsWith';
    } else {
      this.filterSettings = (this.hasRangeFilterSettings()) ? this.getRangeFilterSettings() :
        (this.displayType === this.layoutDisplayType.PLAIN) ? this.alphaFilterSettings : this.numericFilterSettings;
    }

    if (this.onSearchForm) {
      this.subscriptionArray.push(this.modelField.clearState.subscribe(() => {
        this.ngModelChangedValue = undefined;
      }));
    }

    if (this.field.type === 'password') {
      this.isPassword = 'password';
    }

    this.hasFormTextChild = this.isFormText;
    this.alternativeAriaLabel = this.field?.ariaLabel ? this.field?.ariaLabel : this.field?.bindId;

    this.dataFieldAutomationId = `lm-${this.fieldAutomationId}-field`;
  }


  protected setDefaultFieldStyle() {
    if (!this.isCompoundFieldMember
      && (this.displayType === LMTextTypeDisplayEnum.DISPLAY_AS_TAG
        || this.displayType === LMTextTypeDisplayEnum.DISPLAY_AS_TEXT)) {

      if (this.specField && (this.specField.type === AppConstants.NUMERIC
        || this.specField.type === AppConstants.BCD || this.specField.type === AppConstants.PERCENT)) {
        if (!this.layoutObject.fieldStyle) {
          this.layoutObject.fieldStyle = {};
        }

        // force display as text or display as tag number fields to default left align
        if (!this.layoutObject.fieldStyle.align) {
          this.layoutObject.fieldStyle.align = 'left';
        }
      }
    }
  }

  protected updateModelField() {
    if (this.ngModelChangedValue !== undefined) {
      let value = this.ngModelChangedValue;
      if (this.isUpperCase) {
        value = this.ngModelChangedValue.toUpperCase();
      }

      if (this.transformOptions.allowMultiSelections && this.onSearchForm) {
        const allowZero = this.specField.type === AppConstants.BCD ||
          this.specField.type === AppConstants.PERCENT ||
          this.specField.type === AppConstants.NUMERIC;
        const multiValue = this.lmFieldServiceAdapter.lmFormatUtilService.getMultipleFilterFieldValue(value, this.specField.type, this.specField.decSize, this.modelField.precision, allowZero);
        this.modelField.enteredFilterValue = multiValue.enteredFilterValue;
        this.modelField.value = multiValue.formattedFilterValue;
      } else {
        const allowZero = this.specField.type === AppConstants.BCD ||
          this.specField.type === AppConstants.PERCENT ||
          this.specField.type === AppConstants.NUMERIC;
        if (allowZero && value === '') {
          this.modelField.value = value;
        } else {
          this.modelField.toDataValue(value, this.transformOptions);
        }
        this.ngModelChangedValue = undefined;
      }

      // tell the control to check the dirty state
      if (!this.onSearchForm) {
        this.checkApplyState = true;
        this.runApplyState = true;
      }
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.typeObserver?.disconnect();
  }
}
